import { useState, useEffect, useCallback, useRef } from 'react';
import { progressService } from '../services/progress.service';
import { WsProgressUpdate } from '../services/socket.service';

export interface StepData {
  completed: string[];   // student names who completed this step
  inProgress: string[];  // student names currently working on this step
}

export interface StudentPosition {
  name: string;
  highestCompletedIndex: number; // 0-indexed orderIndex of most advanced completed step (-1 if none)
}

/**
 * Hook that tracks per-step completion data (names) and student positions for the PiP widget.
 * 1. Fetches initial data from the dashboard endpoint (per-student per-step)
 * 2. Builds stepData map (names per step) and studentPositions map
 * 3. Exposes handleProgressUpdate for real-time WS updates
 */
export function useProgressPulse(
  sessionId: string,
  steps: { id: string; orderIndex: number }[],
) {
  const [stepData, setStepData] = useState<Map<string, StepData>>(new Map());
  const [totalStudents, setTotalStudents] = useState(0);
  const [studentPositions, setStudentPositions] = useState<Map<string, StudentPosition>>(new Map());
  const [loaded, setLoaded] = useState(false);
  const stepsRef = useRef(steps);
  stepsRef.current = steps;

  // Fetch initial progress from dashboard endpoint
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;

    const fetchInitial = async () => {
      try {
        const dashboard = await progressService.getDashboardData(sessionId);
        if (cancelled) return;

        const newStepData = new Map<string, StepData>();
        const newPositions = new Map<string, StudentPosition>();

        // Initialize all steps with empty arrays
        for (const step of dashboard.session.stepTitles) {
          newStepData.set(step.id, { completed: [], inProgress: [] });
        }

        // Iterate students, build stepData and positions
        for (const student of dashboard.students) {
          let highestCompleted = -1;
          for (const step of student.steps) {
            const entry = newStepData.get(step.stepId);
            if (!entry) continue;
            if (step.status === 'completed') {
              entry.completed.push(student.studentName);
              if (step.orderIndex > highestCompleted) highestCompleted = step.orderIndex;
            } else if (step.status === 'in_progress') {
              entry.inProgress.push(student.studentName);
            }
          }
          newPositions.set(student.studentId, {
            name: student.studentName,
            highestCompletedIndex: highestCompleted,
          });
        }

        setStepData(newStepData);
        setStudentPositions(newPositions);
        setTotalStudents(dashboard.stats.totalStudents);
        setLoaded(true);
      } catch (err) {
        console.error('Failed to fetch initial progress:', err);
      }
    };

    fetchInitial();
    return () => { cancelled = true; };
  }, [sessionId]);

  // Handler for WS progress-update events
  const handleProgressUpdate = useCallback((data: WsProgressUpdate) => {
    const { studentId, stepId, status, studentName } = data;
    const name = studentName || 'Student';

    // Update stepData
    setStepData(prev => {
      const next = new Map(prev);
      const entry = next.get(stepId);
      if (!entry) return prev;
      const updated = { ...entry };

      if (status === 'completed') {
        updated.inProgress = updated.inProgress.filter(n => n !== name);
        if (!updated.completed.includes(name)) {
          updated.completed = [...updated.completed, name];
        }
      } else if (status === 'in_progress') {
        if (!updated.inProgress.includes(name)) {
          updated.inProgress = [...updated.inProgress, name];
        }
      }
      next.set(stepId, updated);
      return next;
    });

    // Update studentPositions on completion
    if (status === 'completed') {
      setStudentPositions(prev => {
        const next = new Map(prev);
        const current = next.get(studentId);
        const stepOrderIndex = stepsRef.current.find(s => s.id === stepId)?.orderIndex ?? -1;
        if (current) {
          if (stepOrderIndex > current.highestCompletedIndex) {
            next.set(studentId, { ...current, highestCompletedIndex: stepOrderIndex });
          }
        } else {
          next.set(studentId, { name, highestCompletedIndex: stepOrderIndex });
        }
        return next;
      });
    }
  }, []);

  return { stepData, totalStudents, studentPositions, loaded, handleProgressUpdate };
}
